<template>
  <div class="animated fadeIn">
    <div class="container">
      <b-modal  ref="modalUnsavedChanges"
                :title="$t('Webportal.Dialog.UnsavedChanges.Title')"
                :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
                :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
                no-close-on-esc
                no-close-on-backdrop
                hide-header-close>
        {{ $t("Webportal.Dialog.UnsavedChanges.Text") }}
      </b-modal>

      <b-modal  ref="modalProcessingNote"
                :title="$t('Webportal.Actions.SurveyImprovement.ProcessingNote.Title')"
                :ok-title="$t('Webportal.Dialog.Ok')"
                ok-only>
        <span v-html="$t('Webportal.Actions.SurveyImprovement.ProcessingNote', { url: $router.resolve({name: 'Notification settings'}).href })"></span>
      </b-modal>

      <form @submit.prevent novalidate="true">
        <b-card class="mb-3" v-bind:class="{ spinner: isBusy }">
          <template slot="header">
            <h5>
              <template v-if="isEditMode">
                {{ $t("Webportal.Actions.SurveyImprovement") }}
                <span v-if="surveyImprovement">
                  #{{ surveyImprovement.SurveyImprovementVisibleId }}
                </span>
                <small  v-if="surveyImprovement"
                        class="float-right col-form-label-sm text-muted">
                  {{ getImprovementCreatedText(surveyImprovement) }}
                </small>
              </template>
              <template v-else>
                {{ $t("Webportal.Actions.SurveyImprovement.Create") }}
              </template>
            </h5>
            <survey-improvement-header  class="pt-2"
                                        :surveyImprovement="surveyImprovement"
                                        :surveyInstance="surveyInstanceData ? surveyInstanceData.SurveyInstance : null"
                                        :statusSelection="!isReadOnly && isEditMode"
                                        :statusHistoryVisible="isEditMode"
                                        :relatedSurveyImprovementsVisible="isEditMode"
                                        @statusChanged="statusChanged" />
          </template>

          <div v-if="!surveyImprovement">
            <span v-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
          </div>
          <div v-if="isInitialized && surveyImprovement">

            <b-list-group flush>
              <b-list-group-item class="pl-0 pr-0">
                <b-form-row>
                  <b-col class="text-left text-header-band" >
                    <div style="padding-left: 8px;">
                      {{ $t('Webportal.Dashboard.KpiOverviewText')}}&nbsp;
                    </div>
                  </b-col>
                </b-form-row>
                <survey-improvement-table :kpis="kpiTableItems">
                </survey-improvement-table>
              </b-list-group-item>

              <b-list-group-item  class="pl-0 pr-0"
                                  v-for="(surveyImprovementEntry, index) of surveyImprovement.Entries"
                                  :key="index">
                <b-button block
                          squared
                          v-b-toggle="`collapse-kpi-${index}`"
                          variant="primary"
                          class="text-left">
                  <b-form-row>
                    <b-col>
                      {{ surveyImprovementEntry.KpiName }}&nbsp;
                    </b-col>
                    <b-col cols="auto align-self-center">
                      <font-awesome-icon size="lg"
                          :icon="['far', surveyImprovementEntry._visible === true ? 'caret-circle-up' : 'caret-circle-down', ]" />
                    </b-col>
                  </b-form-row>
                </b-button>
                <b-collapse class="mt-2"
                            v-model="surveyImprovementEntry._visible"
                            :id="`collapse-kpi-${index}`">
                  <b-form-row>
                    <b-col lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Actions.SurveyImprovement.ActionsToTake') }}
                          <span v-if="surveyInstanceData" 
                            :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea  v-model="surveyImprovementEntry.Remarks"
                                          :placeholder="$t('Webportal.Actions.SurveyImprovement.ActionsToTake.Placeholder')"
                                          :readonly="isReadOnly || surveyImprovementEntry._isReadOnly"
                                          max-rows="6"
                                          v-validate="'required'"
                                          :data-vv-name="`Entries[${index}].Remarks`" />
                      </b-form-group>
                    </b-col>
                    <b-col v-if="isEditMode && needsTranslation" lg>
                      <b-form-group label-size="sm">
                        <template v-slot:label>
                          {{ $t('Webportal.Translated') }}
                          <span v-if="surveyInstanceData" 
                                surveyImprovementEntry
                                :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                        </template>
                        <b-form-textarea  v-model="surveyImprovementEntry.RemarksTranslated"
                                          :placeholder="$t('Webportal.Translated.Placeholder')"
                                          :readonly="isReadOnly"
                                          max-rows="6"
                                          v-validate
                                          :data-vv-name="`Entries[${index}].RemarksTranslated`" />
                      </b-form-group>
                    </b-col>
                  </b-form-row>

                  <file-uploader  ref="entryFileUploader"
                                  :label="$te('Webportal.Actions.SurveyImprovement.Attachments') ? $t('Webportal.Actions.SurveyImprovement.Attachments') : null"
                                  :description="$te('Webportal.Actions.SurveyImprovement.Attachments.Description') ? $t('Webportal.Actions.SurveyImprovement.Attachments.Description') : null"
                                  :value="surveyImprovementEntry.Attachments"
                                  :upload-url="attachmentUploadUrl"
                                  :upload-url-params="{ 'survey-improvement-entry-id': surveyImprovementEntry.SurveyImprovementEntryId, }"
                                  :readonly="isReadOnly"
                                  @is-busy-changed="isBusyChanged(surveyImprovementEntry, '_isFileUploaderBusy', $event)" />

                  <survey-improvement-comments  v-if="isEditMode"
                                                v-model="surveyImprovementEntry.Comments"
                                                :survey-improvement-id="surveyImprovement.SurveyImprovementId"
                                                :survey-improvement-entry-id="surveyImprovementEntry.SurveyImprovementEntryId"
                                                :readonly="isReadOnly"
                                                @is-busy-changed="isBusyChanged(surveyImprovementEntry, '_isCommentsBusy', $event)" />
                </b-collapse>
              </b-list-group-item>
            </b-list-group>
          </div>

          <template slot="footer">
            <div class="float-right">
              
              <button type="button"
                      @click="cancel"
                      class="btn btn-secondary"
                      :disabled="isBusy || isChildBusy">
                <font-awesome-icon  :icon="['far', isEditMode ? 'arrow-left' : 'ban']" />
                {{ isEditMode ? $t("Webportal.Back") : $t("Webportal.Cancel") }}
              </button>

              <button v-if="!isReadOnly"
                      type="button"
                      @click="submit"
                      class="btn btn-primary"
                      :disabled="isSubmitDisabled">
                <font-awesome-icon :icon="['far', 'dot-circle']" />
                {{ isEditMode ? $t("Webportal.Save") : $t("Webportal.Actions.SendRequest") }}
              </button>

            </div>
          </template>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import ActionService from "@/services/ActionService";
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import EventBus from "@/services/EventBus.js";
import FileUploader from "@/components/FileUploader";
import SurveyImprovementComments from "./SurveyImprovementComments";
import SurveyImprovementHeader from "./SurveyImprovementHeader";
import SurveyImprovementMixin from "@/mixins/SurveyImprovementMixin";
import SurveyImprovementTable from './SurveyImprovementTable.vue';

export default {
  name: "SurveyImprovement",

  mixins: [ AppConfig, SurveyImprovementMixin ],

  components: {
    FileUploader,
    SurveyImprovementComments,
    SurveyImprovementHeader,
    SurveyImprovementTable
  },

  props: {
    surveyInstanceId: null,
  },

  data() {
    return {
      isBusy: false,

      surveyImprovement: null,
      surveyInstanceData: null,

      isInitialized: false,
      
      kpiTableItems: [],
      kpiImprovements: []
    };
  },

  computed: {
    isSubmitDisabled() {
      return !this.isFormChanged || !this.isFormValid || this.isBusy || this.isChildBusy;
    },

    isFormChanged() {
      return Object.keys(this.validationFields).some(
        (key) =>
          this.validationFields[key].dirty || this.validationFields[key].changed
      );
    },

    isFormValid() {
      // Here only check if at least one field is changed, other fields can be left empty
      return Object.keys(this.validationFields).some(
        (key) =>
          this.validationFields[key].valid === null ||
          this.validationFields[key].valid
      );
    },

    isEditMode() {
      return this.$route.params.id !== undefined;
    },

    isReadOnly() {
      return !this.surveyImprovement || !this.surveyImprovement.Entries || this.surveyImprovement.IsReadOnly;
    },

    needsTranslation() {
      return this.surveyInstanceData?.SurveyInstance.Survey.DefaultLocalization != 
        this.surveyInstanceData?.SurveyInstance.ClientLocation.DefaultLocalization;
    },

    attachmentUploadUrl() {
      return this.surveyImprovement?.SurveyImprovementId
        ? process.env.BASE_URL +
            `api/action/survey-improvement/${this.surveyImprovement.SurveyImprovementId}/attachment`
        : null;
    },

    isChildBusy() {
      return (
        this.surveyImprovement?.Entries?.some(
          (e) => e._isCommentsBusy || e._isFileUploaderBusy
        )
      );
    },

    surveyImprovementForSubmit() {
      if (!this.isEditMode)
        return this.surveyImprovement;
      else
      {
        var changedEntryRemarksTranslated = Object.entries(this.validationFields)
          .filter(([key, value]) => (key.endsWith('.Remarks') || key.endsWith('.RemarksTranslated')) && value.dirty)
          .map(([key, value]) => key);

        return _.pick(this.surveyImprovement, [
          'SurveyImprovementId', 'SurveyInstanceId', 'StatusId',
          ... changedEntryRemarksTranslated,
          ... changedEntryRemarksTranslated.map(key => key.replace(/\.RemarksTranslated|\.Remarks/, '.SurveyImprovementEntryId'))
          ]);
      }
    },

    surveyImprovementKpis() {
      return this.surveyInstanceData?.Kpis;
    }
  },

  async created() {
    this.initializeTheme();

    if (!this.isEditMode && !this.surveyInstanceId) {
      this.$router.replace({ name: "notfound" });
    }

    if (this.isEditMode) {
      await this.getSurveyImprovement(this.$route.params.id);
      if (this.surveyImprovement) {
        await this.getSurveyInstanceData(this.surveyImprovement.SurveyInstanceId);

        this.kpiTableItems.push(...this.surveyInstanceData.Kpis);
        this.kpiTableItems.push(...this.surveyInstanceData.KpiTotal);

        // get wave text
        for (const [j, kpi] of this.surveyInstanceData.Kpis.entries()) {
          kpi._visible = true;
          // kpi.YTD.Failed !== 0
        }
        for (const [j, kpi] of this.surveyInstanceData.KpiTotal.entries()) {
          kpi._visible = true;
          kpi.KpiName = this.$t('Webportal.Dashboard.Improvements.BonusAchieved');
        }
      }
    } else {
      await this.getSurveyInstanceData(this.surveyInstanceId);

      if (this.surveyInstanceData) {
        if (!this.surveyInstanceData.SurveyInstance.CanCreateSurveyImprovement) {
          const h = this.$createElement;

          this.$bvModal
            .msgBoxOk(
              [
                h("div", {
                  domProps: {
                    innerHTML: this.$t(
                      "Webportal.Dialog.SurveyImprovementCreation.Text",
                      {
                        limitCheckImprovementDays: `${this.surveyInstanceData.SurveyInstance.SurveyImprovementCreationLimitInDays}`,
                        limitCheckRevisionDays: `${this.surveyInstanceData.SurveyInstance.SurveyRevisionCreationLimitInDays}`,
                      }
                    ),
                  },
                }),
              ],
              {
                title: this.$t("Webportal.Dialog.SurveyImprovementCreation.Title"),
                okTitle: this.$t("Webportal.Dialog.Ok"),
                hideHeaderClose: false,
              }
            )
            .then(() => {
              this.$router.go(-1);
            });
        } else {
          // Collect data for KPI-Overview-Table
          this.kpiTableItems.push(...this.surveyInstanceData.Kpis);
          this.kpiTableItems.push(...this.surveyInstanceData.KpiTotal);

          // get wave text
          for (const [j, kpi] of this.surveyInstanceData.Kpis.entries()) {
            kpi._visible = true;
            // kpi.YTD.Failed !== 0
          }
          for (const [j, kpi] of this.surveyInstanceData.KpiTotal.entries()) {
            kpi._visible = true;
            kpi.KpiName = this.$t('Webportal.Dashboard.Improvements.BonusAchieved');
          }

          this.surveyImprovement = {
            SurveyInstanceId: this.surveyInstanceData.SurveyInstance.SurveyInstanceId,
            ExternalSurveyInstanceId: this.surveyInstanceData.SurveyInstance.ExternalSurveyInstanceId,
            StatusId: this.SurveyImprovementStatus.New,
            Entries: [],
          };

          this.surveyImprovement.Entries.push(...this.surveyInstanceData.Kpis.filter(x => !x.HasPassed));
        }
      }
    }

    EventBus.$on("LocalizationChanged", this.localizationChanged);

    this.isInitialized = true;
  },

  beforeDestroy() {
    EventBus.$off("LocalizationChanged", this.localizationChanged);
  },

  beforeRouteLeave(to, from, next) {
    this.unsavedChanges((result) => {
      if (result !== false) next();
    });
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyInstanceData(this.surveyImprovement.SurveyInstanceId);
    },

    getRegion(localization) {
      if (!localization || !localization.includes("-"))
        return "";
      else
        return localization.split("-")[1].toLowerCase();
    },

    isBusyChanged(surveyImprovementEntry, property, value) {
      this.$set(surveyImprovementEntry, property, value);
    },

    unsavedChanges(callback) {
      if (this.isFormChanged || this.isChildBusy) {
        let dialog = this.$refs.modalUnsavedChanges;

        dialog.$off();
        dialog.$on("ok", (_) => callback());
        dialog.$on("cancel", (_) => callback(false));
        dialog.show();
      } else callback();
    },

    statusChanged(statusId) {
      let currentStatus = this.surveyImprovement.StatusId;
      this.surveyImprovement.StatusId = statusId;

      // Submit and undo local status change on error
      this.submit().catch(() => {
        this.surveyImprovement.StatusId = currentStatus;
      });
    },

    getSurveyInstanceData(surveyInstanceId) {
      this.isBusy = true;

      return ActionService.getSurveyInstanceData(surveyInstanceId)
        .then((response) => {
          this.surveyInstanceData = !_.isEmpty(response.data)
            ? response.data
            : null;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    getSurveyImprovement(surveyImprovementId) {
      this.isBusy = true;

      return ActionService.getSurveyImprovement(surveyImprovementId)
        .then((response) => {
          let surveyImprovement = !_.isEmpty(response.data) ? response.data : null;

          if (!_.isEmpty(surveyImprovement?.Entries))
            surveyImprovement.Entries.forEach(entry => {
              entry._visible = true;
              entry._isReadOnly = entry.Remarks != null && entry.Remarks != '';
            });

          this.surveyImprovement = surveyImprovement;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    cancel() {
      this.$router.go(-1);
    },

    async submit() {
      this.isBusy = true;

      try {
        let response = await ActionService.setSurveyImprovement(this.surveyImprovementForSubmit);

        this.$validator.reset();

        this.$bvToast.toast(
          this.$t("Webportal.SaveSuccess.Text", { entity: this.$t("Webportal.Actions.SurveyImprovement") }),
          { autoHideDelay: 2000, variant: "success", noCloseButton: true }
        );

        if (!this.isEditMode) {
          this.$router.replace({
            name: this.$router.currentRoute.name,
            params: { id: response.data.surveyImprovementId },
          });

          if (this.surveyInstanceData.SurveyInstance.ShowSurveyImprovementProcessingNote)
            this.$refs.modalProcessingNote.show();

          // Reload Improvement, also because component is not reloaded on eventual navigation change
          await this.getSurveyImprovement(response.data.surveyImprovementId);

          // Upload all file attachments
          for (const fileUploader of this.$refs.entryFileUploader)
            await fileUploader.uploadFiles();

        } else {
          await this.getSurveyImprovement(response.data.surveyImprovementId);
        }
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", { entity: this.$t("Webportal.Actions.SurveyImprovement"), }),
          { autoHideDelay: 10 * 1000, variant: "danger", noCloseButton: true }
        );

        throw error;
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import  "@/../scss/bmw/variables.scss";

.text-header-band {
  background-color: $bmw-bg21;
  color: $bmw-2021-fg;
  padding-left: 0.325rem;
  padding-right: 0.325rem;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  margin: 0.325rem;
}
</style>
